/**
 * Number formatting shared by the research-design widgets.
 *
 * Everything takes the widget's formatter (`useToolNumber()`), so a translated page
 * writes its own decimal mark, and stays pure so it can be tested in English.
 */

export type NumberFormatter = (
  value: number,
  options?: Intl.NumberFormatOptions
) => string;

/** Shown where a statistic does not exist (no answers, no spread). */
export const DASH = "—";

/** The typographic minus sign. */
export const MINUS = "−";

/** Fixed decimals with a true minus sign; a value that rounds to zero carries no sign. */
export function decimals(
  fmt: NumberFormatter,
  value: number,
  digits = 2
): string {
  if (!Number.isFinite(value)) {
    return DASH;
  }
  const text = fmt(Math.abs(value), {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });
  const roundsToZero = Math.abs(value) < 0.5 * 10 ** -digits;
  return value < 0 && !roundsToZero ? `${MINUS}${text}` : text;
}

/** A share (0–1) as a percentage in the reader's locale ("75%", "75 %", "%75"). */
export function percentText(
  fmt: NumberFormatter,
  share: number | null,
  digits = 1
): string {
  if (share === null || !Number.isFinite(share)) {
    return DASH;
  }
  return fmt(share, { style: "percent", maximumFractionDigits: digits });
}

/** A run of codes: "1–2", or "3" when it is a single code. */
export function codeRange(from: number, to: number): string {
  return from === to ? String(from) : `${from}–${to}`;
}

const SUPERSCRIPT_DIGITS = "⁰¹²³⁴⁵⁶⁷⁸⁹";
const DIGIT = /\d/g;

/** A whole number as superscript digits, for exponents in plain-text working (0.5⁶). */
export function superscript(value: number): string {
  return String(Math.trunc(value)).replace(
    DIGIT,
    (digit) => SUPERSCRIPT_DIGITS[Number(digit)]
  );
}
